// Phase E2 (May 2026 — reversibility-first architecture).
//
// Type-to-confirm gate. Used by the destructive-action confirm
// (B3's DestructiveActionConfirm) when the action policy
// (E1's action-policies) declares `confirms: 'type_to_confirm'` OR
// when the affected count exceeds the policy's threshold.
//
// Pattern: the user must literally type the supplied phrase
// ("MERGE 12 CONTACTS") to enable the submit button. Slows down
// muscle-memory mistakes on actions whose blast radius warrants the
// extra friction (bulk merge / bulk reassign / permanent delete).
//
// The phrase is uppercase by convention — comparison is also
// uppercase so the user's typing is forgiving on case but strict on
// literal content.

import { useState, type ReactNode } from "react";
import { Check } from "lucide-react";

import { matches } from "./type-to-confirm-helpers";

export { matches } from "./type-to-confirm-helpers";

export interface TypeToConfirmGateProps {
  /**
   * The phrase the user must type. Should describe the action +
   * scope, e.g. "MERGE 12 CONTACTS" or "DELETE PERMANENTLY".
   * Uppercase by convention.
   */
  phrase: string;
  /** Plain-English explanation shown above the input. */
  explanation: ReactNode;
  /**
   * Render-prop the parent uses to know whether to enable its
   * submit button. The gate calls this with `true` once the user
   * types the matching phrase, `false` otherwise.
   */
  onPassChange: (passed: boolean) => void;
  /** Optional disabled state — passes through to the input. */
  disabled?: boolean;
}

export function TypeToConfirmGate({
  phrase,
  explanation,
  onPassChange,
  disabled,
}: TypeToConfirmGateProps) {
  const [value, setValue] = useState("");

  function handleChange(next: string) {
    setValue(next);
    onPassChange(matches(next, phrase));
  }

  const passed = matches(value, phrase);
  return (
    <div
      role="group"
      aria-label="Type-to-confirm gate"
      style={{
        background: "rgba(192, 57, 43, 0.06)",
        border: "1px solid rgba(192, 57, 43, 0.3)",
        borderRadius: 6,
        padding: "10px 12px",
        marginTop: 12,
      }}
    >
      <div
        style={{
          fontSize: 12,
          color: "var(--danger, #c0392b)",
          marginBottom: 6,
          lineHeight: 1.5,
        }}
      >
        <strong>Confirm by typing the phrase below.</strong> {explanation}
      </div>
      <div style={{ marginBottom: 6, fontFamily: "monospace", fontSize: 12 }}>
        Phrase: <strong>{phrase}</strong>
      </div>
      <input
        type="text"
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={`Type: ${phrase}`}
        disabled={disabled}
        autoComplete="off"
        spellCheck={false}
        style={{
          width: "100%",
          padding: "6px 8px",
          border: passed
            ? "2px solid #22c55e"
            : "1px solid var(--mid-gray)",
          borderRadius: "var(--radius-compact)",
          fontSize: 14,
          fontFamily: "monospace",
        }}
      />
      <div
        style={{
          fontSize: 11,
          marginTop: 4,
          color: passed ? "#15803d" : "var(--danger, #c0392b)",
        }}
      >
        {passed ? (
          <>
            <Check size={14} strokeWidth={1.5} aria-hidden /> Phrase matches —
            submit enabled
          </>
        ) : value.length > 0 ? (
          "Phrase doesn't match yet"
        ) : (
          "Awaiting input"
        )}
      </div>
    </div>
  );
}

